import { useState, useRef, useCallback, useEffect } from 'react';
import { Order } from '../../dto/Profile.dto';
import './styles/OrderdetailsSummary.css';
import './styles/OrderdetailsSummaryMobile.css';

const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 1024; // Covers mobile, tablet, and iPad
};

interface OrderDetailsSummaryProps {
  order: Order | null;
}

const OrderDetailsSummary: React.FC<OrderDetailsSummaryProps> = ({ order }) => {
  const [expandedTitles, setExpandedTitles] = useState<{[key: string]: boolean}>({});
  const [textTruncated, setTextTruncated] = useState<{[key: string]: boolean}>({});
  const [, setIsMobile] = useState(false);
  const titleRefs = useRef<{[key: string]: HTMLHeadingElement | null}>({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if text is truncated
  const checkTextTruncation = useCallback((el: HTMLHeadingElement | null, id: string) => {
    if (el && !textTruncated[id]) {
      titleRefs.current[id] = el;
      const isTruncated = el.scrollHeight > el.clientHeight;
      if (isTruncated !== textTruncated[id]) {
        setTextTruncated(prev => ({
          ...prev,
          [id]: isTruncated
        }));
      }
    }
  }, [textTruncated]);

  // Add resize observer to handle window resizing
  useEffect(() => {
    const handleResize = () => {
      Object.entries(titleRefs.current).forEach(([id, el]) => {
        if (el) {
          const isTruncated = el.scrollHeight > el.clientHeight;
          setTextTruncated(prev => ({
            ...prev,
            [id]: isTruncated
          }));
        }
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!order) {
    return <div>Loading order details...</div>;
  }

  // Toggle expanded state for a title
  const toggleTitleExpand = (itemId: string) => {
    setExpandedTitles(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const formatTime = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleTimeString('en-IN', options).toLowerCase();
  };


  interface TrackingStep {
    id: string;
    title: string;
    status: 'completed' | 'pending' | 'cancelled';
    timestamp: string;
  }

  // Get the appropriate time based on order status
  const getCancelledTime = () => {
    if (order.trackingInfo?.trackingHistory) {
      const cancelledEntry = order.trackingInfo.trackingHistory.find(
        entry => entry.status === 'cancelled'
      );
      if (cancelledEntry) {
        return cancelledEntry.timestamp;
      }
    }
    return order.updatedAt || new Date().toISOString();
  };

  const getDeliveryTime = () => {
    if (order.trackingInfo?.actualDeliveryTime) {
      return order.trackingInfo.actualDeliveryTime;
    }
    if (order.trackingInfo?.trackingHistory) {
      const deliveredEntry = order.trackingInfo.trackingHistory.find(
        entry => entry.status === 'delivered'
      );
      if (deliveredEntry) {
        return deliveredEntry.timestamp;
      }
    }
    return new Date().toISOString();
  };

  const trackingSteps: TrackingStep[] = order.status === 'cancelled' 
    ? [
        {
          id: 'ordered',
          title: `Order Placed at ${formatTime(order.createdAt)}`,
          status: 'completed',
          timestamp: order.createdAt
        },
        {
          id: 'cancelled',
          title: `Order Cancelled at ${formatTime(getCancelledTime())}`,
          status: 'cancelled',
          timestamp: getCancelledTime()
        }
      ]
    : [
        {
          id: 'ordered',
          title: `Order Placed at ${formatTime(order.createdAt)}`,
          status: 'completed',
          timestamp: order.createdAt
        },
        {
          id: 'delivered',
          title: `Order Delivered at ${formatTime(getDeliveryTime())}`,
          status: order.status === 'delivered' ? 'completed' : 'pending',
          timestamp: getDeliveryTime()
        }
      ];

  return (
    <div className="order-summary-container">
      <div className="order-summary-card">
        <h2 className="order-summary-title">Order Summary</h2>
        
        <div className="order-items-list">
          {order.items?.map((item, index) => (
            <div key={`${item.product._id}-${index}`} className="order-item">
              <img 
                src={item.product.imageUrl} 
                alt={item.variantTitle} 
                className="order-item-image" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"%3E%3Crect width="80" height="80" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%239ca3af"%3EProduct%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="order-item-details">
                <div style={{ position: 'relative' }}>
                  <div>
                    <h3 
                      ref={el => checkTextTruncation(el, item.product._id)}
                      className={`order-item-name ${expandedTitles[item.product._id] ? 'expanded' : ''}`}
                      style={{ position: 'relative', display: 'inline-block', width: '100%' }}
                    >
                      {item.variantTitle}
                    </h3>
                    {textTruncated[item.product._id] && (
                      <span 
                        className="show-more-text"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTitleExpand(item.product._id);
                        }}
                      >
                        {expandedTitles[item.product._id] ? 'Show less' : 'Show more'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="order-item-quantity-badge">Quantity: {item.quantity}</div>
              </div>
              <div className="order-item-price">₹{item.price.toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="order-tracking-section">
          <h3 className="tracking-title">Order Tracking</h3>
          <div className="tracking-timeline">
            {trackingSteps.map((step, index) => (
              <div key={step.id} className={`tracking-step ${step.status}`}>
                <div className="tracking-icon-wrapper">
                  <div className="tracking-icon">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {index < trackingSteps.length - 1 && <div className="tracking-line"></div>}
                </div>
                <div className="tracking-content">
                  <p className="tracking-status">{step.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsSummary;